import React, { useState, useEffect } from 'react';
import './marvel84-roller.css';

const Marvel84Roller = ({ socket, username, roomName }) => {
  const [selectedColumn, setSelectedColumn] = useState('Typical');
  const [rollResult, setRollResult] = useState(null);
  const [colorResult, setColorResult] = useState('');
  const [rollHistory, setRollHistory] = useState([]);
  const [isRolling, setIsRolling] = useState(false);

  // Universal Chart columns (ability ranks)
  const columns = [
    'Shift 0',
    'Feeble',
    'Poor',
    'Typical',
    'Good',
    'Excellent',
    'Remarkable',
    'Incredible',
    'Amazing',
    'Monstrous',
    'Unearthly',
    'Shift X',
    'Shift Y',
    'Shift Z',
    'Class 1000',
    'Class 3000',
    'Class 5000',
    'Beyond'
  ];

  // Universal Chart thresholds for each column
  // Format: { white: max, green: max, yellow: max, red: max }
  // White = 1 to (green-1), Green = green to (yellow-1), etc.
  const columnThresholds = {
    'Shift 0': {white: 65, green: 94, yellow: 99, red: 100 },
    'Feeble': { white: 60, green: 90, yellow: 99, red: 100 },
    'Poor': { white: 55, green: 85, yellow: 99, red: 100 },
    'Typical': { white: 50, green: 80, yellow: 97, red: 100 },
    'Good': { white: 45, green: 75, yellow: 97, red: 100 },
    'Excellent': { white: 40, green: 70, yellow: 94, red: 100 },
    'Remarkable': { white: 35, green: 65, yellow: 94, red: 100 },
    'Incredible': { white: 30, green: 60, yellow: 90, red: 100 },
    'Amazing': { white: 25, green: 55, yellow: 90, red: 100 },
    'Monstrous': { white: 20, green: 50, yellow: 85, red: 100 },
    'Unearthly': { white: 15, green: 45, yellow: 85, red: 100 },
    'Shift X': { white: 10, green: 40, yellow: 80, red: 100 },
    'Shift Y': { white: 6, green: 40, yellow: 80, red: 100 },
    'Shift Z': { white: 3, green: 35, yellow: 75, red: 100 },
    'Class 1000': { white: 1, green: 35, yellow: 75, red: 100 },
    'Class 3000': { white: 1, green: 30, yellow: 70, red: 100 },
    'Class 5000': { white: 1, green: 25, yellow: 65, red: 100 },
    'Beyond': { white: 1, green: 20, yellow: 60, red: 100 }
  };

  // Universal Chart color mapping based on percentile roll and selected column
  const determineColor = (roll, column) => {
    const thresholds = columnThresholds[column] || columnThresholds['Typical'];
    
    if (roll <= thresholds.white) {
      return { color: 'White', label: 'White (Failure)' };
    } else if (roll <= thresholds.green) {
      return { color: 'Green', label: 'Green (Success)' };
    } else if (roll <= thresholds.yellow) {
      return { color: 'Yellow', label: 'Yellow (Good Success)' };
    } else {
      return { color: 'Red', label: 'Red (Amazing Success)' };
    }
  };

  // Listen for shared rolls
  useEffect(() => {
    if (!socket) return;

    const onSharedRoll = (payload) => {
      // optional guard: only accept current room
      if (payload?.roomName && roomName && payload.roomName !== roomName) return;

      setRollHistory((prev) => [
        {
          roll: payload.roll,
          color: payload.color,
          column: payload.column,
          timestamp: payload.timestamp ? new Date(payload.timestamp) : new Date(),
          by: payload.by || "Someone",
          id: payload.id,
        },
        ...prev.slice(0, 9),
      ]);
    };

    socket.on("marvelRoll", onSharedRoll);
    return () => socket.off("marvelRoll", onSharedRoll);
  }, [socket, roomName]);

  const rollDice = () => {
    setIsRolling(true);
    
    // Animate the roll with multiple random numbers
    let animationCount = 0;
    const animationInterval = setInterval(() => {
      const tempRoll = Math.floor(Math.random() * 100) + 1;
      setRollResult(tempRoll);
      const tempColor = determineColor(tempRoll, selectedColumn);
      setColorResult(tempColor);
      animationCount++;
      
      if (animationCount >= 10) {
        clearInterval(animationInterval);
        setIsRolling(false);
        
        // Final roll
        const finalRoll = Math.floor(Math.random() * 100) + 1;
        const finalColor = determineColor(finalRoll, selectedColumn);
        setRollResult(finalRoll);
        setColorResult(finalColor);
        
        const payload = {
        id: (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`),
        roomName,
        by: username || "Someone",
        roll: finalRoll,
        color: finalColor,
        column: selectedColumn,
        timestamp: new Date().toISOString(),
        };

// Broadcast to room (if available)
if (socket && roomName) {
  socket.emit("marvelRoll", payload);
}
      }
    }, 50);
  };

  const clearHistory = () => {
    setRollHistory([]);
    setRollResult(null);
    setColorResult('');
  };

  const getColorClass = (color) => {
    const colorMap = {
      'White': 'dice-white',
      'Green': 'dice-green',
      'Yellow': 'dice-yellow',
      'Red': 'dice-red'
    };
    return colorMap[color] || '';
  };


  return (
    <div className="dice-roller-container">
      <div className="dice-roller-header">
        {/*<h2>Marvel Super Heroes</h2>*/}
      </div>

      <div className="column-selector-area">
        <label htmlFor="column-select" className="column-label">
          Ability Rank:
        </label>
        <select
          id="column-select"
          className="column-select"
          value={selectedColumn}
          onChange={(e) => setSelectedColumn(e.target.value)}
          disabled={isRolling}
        >
          {columns.map((col) => (
            <option key={col} value={col}>
              {col}
            </option>
          ))}
        </select>
      </div>

      <div className="dice-roll-area">
        <button 
          className={`roll-button ${isRolling ? 'rolling' : ''}`}
          onClick={rollDice}
          disabled={isRolling}
        >
          {isRolling ? 'Rolling...' : 'Roll'}
        </button>

        {rollResult !== null && (
          <div className={`roll-result ${getColorClass(colorResult.color)} ${isRolling ? 'rolling' : ''}`}>
            <div className="roll-column-info">Column: {selectedColumn}</div>
            <div className="roll-number">{rollResult}</div>
            <div className="roll-color-label">{colorResult.label}</div>
          </div>
        )}
      </div>

      {rollHistory.length > 0 && (
        <div className="roll-history">
          <div className="history-header">
            <h3>Roll History</h3>
            <button className="clear-button" onClick={clearHistory}>
              Clear
            </button>
          </div>
          <div className="history-list">
            {rollHistory.map((item, index) => (
              <div 
                key={index} 
                className={`history-item ${getColorClass(item.color.color)}`}
              >
                <span className="history-roll">{item.roll}</span>
                <span className="history-column">{item.column}</span>
                <span className="history-color">{item.color.label}</span>
                <span className="history-time">
                  {item.timestamp.toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Marvel84Roller;
